// Managed announcements (plan docs/plans/managed-announcement-system).
//
// One announcement row is authored on exactly one of three surfaces — platform,
// partner, tenant — and `scope` says which. The desktop reads the UNION of the
// three, so the member-facing DTO carries `scope` as a label rather than as a
// branch: the renderer never switches on it.
//
// The body is PLAIN TEXT. Nothing renders it as markdown or HTML, which is why
// the authoring surface only has to strip control characters instead of
// sanitizing a markup subset.

/** How loudly the desktop should present the notice. */
export type AnnouncementSeverity = 'info' | 'warning' | 'critical';

/** draft -> published -> archived. Illegal transitions are 409, never a no-op. */
export type AnnouncementStatus = 'draft' | 'published' | 'archived';

/** Which authoring surface owns the row (and therefore who can see it). */
export type AnnouncementScope = 'platform' | 'partner' | 'tenant';

export const ANNOUNCEMENT_SEVERITIES: readonly AnnouncementSeverity[] = [
  'info',
  'warning',
  'critical',
];

export const ANNOUNCEMENT_STATUSES: readonly AnnouncementStatus[] = [
  'draft',
  'published',
  'archived',
];

export const ANNOUNCEMENT_SCOPES: readonly AnnouncementScope[] = [
  'platform',
  'partner',
  'tenant',
];

/** Longest accepted title/body, mirrored by the DB CHECK constraints. */
export const ANNOUNCEMENT_TITLE_MAX = 120;
export const ANNOUNCEMENT_BODY_MAX = 4000;

/** Default and ceiling for `GET /v1/me/announcements`. The admin-wide
 *  `MAX_LIST_LIMIT` (200) is far too generous for a notification panel that
 *  renders every row, so the member surface clamps to its own ceiling. */
export const ME_ANNOUNCEMENTS_DEFAULT_LIMIT = 20;
export const ME_ANNOUNCEMENTS_MAX_LIMIT = 50;

export function isAnnouncementSeverity(
  value: unknown
): value is AnnouncementSeverity {
  return ANNOUNCEMENT_SEVERITIES.includes(value as AnnouncementSeverity);
}

export function isAnnouncementStatus(
  value: unknown
): value is AnnouncementStatus {
  return ANNOUNCEMENT_STATUSES.includes(value as AnnouncementStatus);
}

export function isAnnouncementScope(value: unknown): value is AnnouncementScope {
  return ANNOUNCEMENT_SCOPES.includes(value as AnnouncementScope);
}

/** An announcement as the AUTHORING consoles see it (drafts included). */
export interface Announcement {
  id: string;
  scope: AnnouncementScope;
  /** Set iff `scope === 'tenant'`. */
  tenantId: string | null;
  /** Set iff `scope === 'partner'`. */
  partnerId: string | null;
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  status: AnnouncementStatus;
  /** ISO timestamp; null until published. */
  publishedAt: string | null;
  /** ISO timestamp; null means it never expires. */
  expiresAt: string | null;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAnnouncementRequest {
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  /** ISO timestamp in the future, or null/omitted for no expiry. */
  expiresAt?: string | null;
}

/** Partial edit. `scope`/`tenantId`/`partnerId` are route-injected and immutable,
 *  so they are absent by construction. An empty patch is a 400. */
export interface PatchAnnouncementRequest {
  title?: string;
  body?: string;
  severity?: AnnouncementSeverity;
  expiresAt?: string | null;
}

export interface AnnouncementListQuery {
  status?: AnnouncementStatus;
  limit?: number;
  offset?: number;
}

/** One row of the member-facing (desktop) list. */
export interface MeAnnouncementItem {
  id: string;
  /** Where it came from. Present from the first contract so the panel can label
   *  the source later without a server change. */
  scope: AnnouncementScope;
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  /** Always set: only published rows are visible. */
  publishedAt: string;
  expiresAt: string | null;
  unread: boolean;
}

export interface MeAnnouncementsResponse {
  items: MeAnnouncementItem[];
  /** Unread count across every VISIBLE active announcement — deliberately NOT
   *  limited to the returned page, because it drives the titlebar badge. */
  unreadCount: number;
  /** Visible active total, before limit/offset. */
  total: number;
  limit: number;
  offset: number;
}

export interface MarkAllAnnouncementsReadResult {
  ok: true;
  /** Rows this call newly marked read (already-read rows are not counted). */
  markedCount: number;
}
